import { useNavigate } from 'react-router-dom';
import { NotifyToggleSwitch } from '../../../notifications/NotifyToggleSwitch';
import styles from '../ExpressModeModal.module.css';

interface PendingRedownload {
  selectedScenarioIds: string[];
  exportType: 'zip' | 'concat';
  identifier: string;
}

interface StepExportProps {
  scenarioId: string | null;
  allScenarioIds: string[];
  isExporting: boolean;
  exportComplete: false | 'zip' | 'concat';
  exportStatus: string;
  pendingRedownload?: PendingRedownload | null;
  mp4DownloadUrl?: string | null;
  mp4Generating?: boolean;
  /** Seedance 動画を生成中か (Step 4 入り口で動画生成が走る) */
  isAutoGenerating?: boolean;
  videosDone?: number;
  videosTotal?: number;
  onExportZip: () => void;
  onExportConcat: () => void;
  onBackToScenarios: () => void;
  onClose: () => void;
}

export function StepExport({
  scenarioId,
  allScenarioIds,
  isExporting,
  exportComplete,
  exportStatus,
  pendingRedownload,
  mp4DownloadUrl,
  mp4Generating,
  isAutoGenerating,
  videosDone,
  videosTotal,
  onBackToScenarios,
  onExportZip,
  onExportConcat,
  onClose,
}: StepExportProps) {
  const navigate = useNavigate();
  const isSingleScenario = allScenarioIds.length <= 1;
  // secondaryボタン内の白いSVGアイコンをグレーに変換するフィルター
  const secondaryIconStyle = { filter: 'brightness(0) opacity(0.4)' } as const;

  const handleGoToScenario = () => {
    if (!scenarioId) return;
    onClose();
    if (allScenarioIds.length > 1) {
      // 複数シナリオ: 最初のシナリオをパスに、残りをクエリパラメータで渡す
      const rest = allScenarioIds.filter(id => id !== scenarioId);
      navigate(`/studio/video/${scenarioId}?load_ids=${rest.join(',')}`);
    } else {
      navigate(`/studio/video/${scenarioId}`);
    }
  };

  // スリープ復帰時の再ダウンロードUI
  if (pendingRedownload && !exportComplete && !isExporting) {
    const handleRedownload = () => {
      if (pendingRedownload.exportType === 'concat') {
        onExportConcat();
      } else {
        onExportZip();
      }
    };

    return (
      <div className={styles.stepContent}>
        <h2 className={styles.stepTitle}>ダウンロードが中断されました</h2>
        <div className={styles.exportSection}>
          <p className={styles.stepDescription}>
            スリープ等により動画のダウンロードが中断されました。
            ボタンを押して再ダウンロードしてください。
          </p>
          <div className={styles.exportActions}>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={handleRedownload}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              再ダウンロード
            </button>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={onClose}
            >
              閉じる
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Seedance 動画生成中 (Step 4 入り口) はダウンロードでなく進捗を表示する
  if (isAutoGenerating) {
    const total = videosTotal ?? 0;
    const done = videosDone ?? 0;
    return (
      <div className={styles.stepContent}>
        {/* タイトル行。Step 3 と同様に完了通知トグルを同じ行の右端に出す (別タブ待機向け・紫トーン)。 */}
        <div className={`${styles.generatingHeaderRow} ${styles.notifyTogglePurple}`}>
          <h2 className={styles.stepTitle}>動画を生成中</h2>
          <NotifyToggleSwitch variant="panel" />
        </div>
        <div className={styles.loadingSection}>
          <div className={styles.spinnerLarge} />
          <p className={styles.loadingMessage}>動画を生成しています...</p>
          <p className={styles.loadingSubMessage}>
            {total > 0 ? `${done} / ${total} 案 完了` : ''}（数分かかります）
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.stepContent}>
      <h2 className={styles.stepTitle}>プレビュー動画</h2>

      <div className={styles.exportSection}>
        {!scenarioId ? (
          <p className={styles.errorMessage}>シナリオが見つかりません</p>
        ) : (
          <>
            <p className={styles.stepDescription}>
              生成した動画をMP4として書き出すか、スタジオ画面で詳細を確認・編集できます。
            </p>

            {isExporting && (
              <div className={styles.loadingSection}>
                <div className={styles.spinnerLarge} />
                <p className={styles.loadingMessage}>{exportStatus || '動画を生成しています...'}</p>
              </div>
            )}

            {!isExporting && (
              <div className={styles.exportActions}>
                {/* 連結ダウンロード: 複数シナリオのみ表示 */}
                {!isSingleScenario && (
                  <button
                    type="button"
                    className={exportComplete ? styles.secondaryButton : styles.primaryButton}
                    onClick={onExportConcat}
                    disabled={mp4Generating}
                    style={mp4Generating ? { opacity: 0.6 } : undefined}
                  >
                    {mp4Generating ? (
                      <>
                        <div className={styles.spinnerTiny} />
                        連結された動画を生成中...
                      </>
                    ) : (
                      <>
                        <img src="/video_file_download.svg" alt="" width="18" height="18" style={exportComplete ? secondaryIconStyle : undefined} />
                        {mp4DownloadUrl ? '連結された動画をダウンロード' : '連結された動画ファイルを生成'}
                      </>
                    )}
                  </button>
                )}

                {/* 個別/単一ダウンロード */}
                <button
                  type="button"
                  className={isSingleScenario && !exportComplete ? styles.primaryButton : styles.secondaryButton}
                  onClick={onExportZip}
                >
                  <img src={isSingleScenario ? "/video_file_download.svg" : "/zip_file_check.svg"} alt="" width="18" height="18" style={!(isSingleScenario && !exportComplete) ? secondaryIconStyle : undefined} />
                  {isSingleScenario
                    ? (mp4DownloadUrl ? '動画をダウンロード' : '動画を生成')
                    : 'シナリオ別の動画をダウンロード'}
                </button>

                {/* スタジオボタン */}
                <button
                  type="button"
                  className={exportComplete ? styles.primaryButton : styles.secondaryButton}
                  onClick={handleGoToScenario}
                >
                  <img src="/video_edit_cut.svg" alt="" width="18" height="18" style={!exportComplete ? secondaryIconStyle : undefined} />
                  スタジオでシナリオを確認する
                </button>
              </div>
            )}

            {onBackToScenarios && (
              <button type="button" className={styles.backLink} onClick={onBackToScenarios}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
                絵コンテの選択に戻る
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
